import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { PageHero } from "@/components/ui/PageHero";
import { Pill } from "@/components/ui/Pill";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";

/**
 * `/for-business` — page for buyers: customers of manufacturers that
 * sell through Forgmatic. CTA goes to `/sign-up-team` (PR-D).
 *
 * KAN-1953: no SSO tile. Tenant SSO (OIDC/SAML) signs in the
 * manufacturer's own staff; a buyer's company members cannot use it.
 */
export default function ForBusiness(): JSX.Element {
  const { t } = useTranslation("saas");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  useSeo({
    title: t("forBusiness.seo.title"),
    description: t("forBusiness.seo.description"),
    path: "/for-business",
    lang,
  });

  const valueProps: ReadonlyArray<{ key: string }> = [
    { key: "approvals" },
    { key: "shared" },
    { key: "reports" },
    { key: "rebooks" },
  ];

  return (
    <>
      <PageHero innerClassName="max-w-6xl px-7 pb-14 pt-12 md:pt-16">
        <Pill tone="accent">{t("forBusiness.eyebrow")}</Pill>
        <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight text-ink md:text-5xl">
          {t("forBusiness.title")}
        </h1>
        <p className="mt-4 max-w-2xl text-base text-ink-2 md:text-lg">
          {t("forBusiness.lead")}
        </p>
        <p
          className="mt-3 max-w-2xl text-sm text-ink-3"
          data-testid="for-business-availability"
        >
          {t("forBusiness.availability")}
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to={`/${lang}/sign-up-team`}
            className="inline-flex items-center rounded-md bg-ink px-5 py-2.5 text-sm font-medium text-bg shadow-sm transition-colors hover:bg-ink-2"
            data-testid="for-business-cta-primary"
          >
            {t("forBusiness.ctaPrimary")}
          </Link>
        </div>
      </PageHero>

      <main className="mx-auto max-w-6xl px-7 pb-20 pt-14">
        <section data-testid="for-business-value-props">
          <header className="max-w-3xl">
            <h2 className="text-2xl font-semibold tracking-tight text-ink md:text-3xl">
              {t("forBusiness.valueProps.heading")}
            </h2>
            <p className="mt-2 text-ink-2">{t("forBusiness.valueProps.lead")}</p>
          </header>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {valueProps.map(({ key }) => (
              <article
                key={key}
                className="rounded-lg border border-border bg-surface p-5"
                data-testid={`for-business-vp-${key}`}
              >
                <h3 className="text-base font-semibold text-ink">
                  {t(`forBusiness.valueProps.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm text-ink-2">
                  {t(`forBusiness.valueProps.${key}.body`)}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="mt-20 rounded-xl border border-border bg-surface px-6 py-10 text-center"
          data-testid="for-business-final-cta"
        >
          <h2 className="text-2xl font-semibold tracking-tight text-ink md:text-3xl">
            {t("forBusiness.finalCta.heading")}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-ink-2">
            {t("forBusiness.finalCta.lead")}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to={`/${lang}/sign-up-team`}
              className="inline-flex items-center rounded-md bg-ink px-5 py-2.5 text-sm font-medium text-bg shadow-sm hover:bg-ink-2"
            >
              {t("forBusiness.finalCta.primary")}
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
